// Demo organisation settings, as ledger.organisation_settings and
// ledger.update_organisation: the same checks, against demo books that have
// postings and a filed VAT return (so the currency and year are fixed).
import { checkVatNumber, firstYearRange, followsOn, MONTHS, monthsIn, vatPeriod, type OrgPatch } from '../screens/settings/orgSettings';
import { demoContactCodes, demoContactsWithoutCode, nextContactCode } from './mockDocuments';
import { demoJurisdiction } from './mockMtd';
import type { LedgerSource, OrganisationSettings } from './source';

type Part = Pick<LedgerSource, 'organisationSettings' | 'updateOrganisation'>;
const delay = <T,>(v: T, ms = 80): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (m: string) => Promise.reject(new Error(m));

const DEMO_TODAY = '2026-10-12';
const fmt = (d: string) => `${Number(d.slice(8))} ${MONTHS[Number(d.slice(5, 7)) - 1]!.slice(0, 3)} ${d.slice(0, 4)}`;

let store: { j: 'IE' | 'UK'; s: OrganisationSettings; posted: boolean } | null = null;

function fresh(j: 'IE' | 'UK'): OrganisationSettings {
  const uk = j === 'UK';
  const lastFiled = uk ? '2026-06-30' : '2026-08-31';
  return withDerived({
    legal_name: 'Blaze Fire Systems Ltd', trading_name: 'Blaze Fire', entity_type: 'company',
    registered_number: uk ? '09876543' : '612345', registered_office: uk ? '4 Castle Street, Belfast BT1 1HB' : 'Unit 4, Swords Business Park, Swords, Co. Dublin K67 E2X4',
    address: null, contact_email: 'accounts@blazefire.ie', contact_phone: '+353 1 890 4410', website: 'blazefire.ie',
    jurisdiction: j, base_currency: uk ? 'GBP' : 'EUR',
    vat_registered: true, vat_number: uk ? 'GB123456789' : 'IE1234567T', vat_basis: 'invoice',
    vat_frequency: uk ? 'quarterly' : 'bimonthly', vat_period_start_month: 1,
    year_start_month: 1, first_year: 'FY2026', first_year_starts: '2026-01-01', setup_completed: true,
    locks: {}, last_vat_return_to: lastFiled, next_vat_period: null,
  }, true);
}

function withDerived(s: OrganisationSettings, posted: boolean): OrganisationSettings {
  const filed = s.last_vat_return_to ?? null;
  const next = new Date(`${filed ?? DEMO_TODAY}T12:00:00Z`);
  if (filed) next.setUTCDate(next.getUTCDate() + 1);
  const p = vatPeriod(next.toISOString().slice(0, 10), s.vat_frequency, s.vat_period_start_month);
  // As vat_period_for: UK one month and seven days after the period; Ireland the 23rd (via ROS).
  const n = monthsIn(s.vat_frequency);
  const due = new Date(`${p.from}T12:00:00Z`);
  due.setUTCMonth(due.getUTCMonth() + n + (s.jurisdiction === 'UK' ? 1 : 0));
  due.setUTCDate(due.getUTCDate() + (s.jurisdiction === 'UK' ? 6 : 22));
  return {
    ...s,
    locks: {
      jurisdiction: 'Set when the books were created: it chose the chart of accounts and the VAT codes.',
      base_currency: posted ? 'Fixed now that entries have been posted.' : null,
      year: posted ? 'Fixed now that entries have been posted. Ask your accountant about changing a year end.' : null,
      vat_periods: filed ? `VAT periods must follow on from the last return filed (to ${fmt(filed)}).` : null,
    },
    next_vat_period: { from: p.from, to: p.to, label: p.label, due: due.toISOString().slice(0, 10) },
  };
}

function current() {
  const j = demoJurisdiction() === 'UK' ? 'UK' : 'IE';
  if (!store || store.j !== j) {
    store = { j, s: fresh(j), posted: true };
    // Opening the demo with ?demo=new-books shows the first-run setup.
    if (typeof location !== 'undefined' && /[?&]demo=new-books\b/.test(location.search)) demoNewBooks();
  }
  return store;
}

/** For tests and screenshots: demo books just created (nothing posted, not set up). */
export function demoNewBooks(on = true) {
  const st = current();
  if (!on) { store = null; return; }
  st.posted = false;
  st.s = withDerived({ ...st.s, trading_name: null, registered_number: null, registered_office: null, contact_email: null, contact_phone: null,
    website: null, vat_number: null, setup_completed: false, last_vat_return_to: null }, false);
}

/** What org_context adds for the demo. */
export function demoOrgContextExtras() {
  const s = current().s;
  return { vat_registered: s.vat_registered, vat_period_start_month: s.vat_period_start_month, year_start_month: s.year_start_month,
    setup_completed: s.setup_completed, legal_name: s.legal_name, trading_name: s.trading_name ?? null, vat_number: s.vat_number ?? null,
    vat_frequency: s.vat_frequency, vat_basis: s.vat_basis, entity_type: s.entity_type, base_currency: s.base_currency };
}

const KEYS = ['legal_name', 'trading_name', 'entity_type', 'registered_number', 'company_number', 'registered_office', 'address', 'contact_email',
  'contact_phone', 'website', 'jurisdiction', 'base_currency', 'vat_registered', 'vat_number', 'vat_basis', 'vat_frequency',
  'vat_period_start_month', 'year_start_month', 'first_year', 'setup_completed', 'contact_codes'];
const withCodes = (s: OrganisationSettings): OrganisationSettings => ({ ...s, contact_codes: { ...demoContactCodes, contacts_without_code: demoContactsWithoutCode().length } });
const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);

export const demoOrganisation: Part = {
  organisationSettings: () => delay(withCodes(current().s)),
  updateOrganisation: (patchIn) => {
    const st = current();
    const p = patchIn as OrgPatch & { company_number?: string | null; jurisdiction?: string };
    const s = { ...st.s };
    for (const k of Object.keys(p)) if (!KEYS.includes(k)) return fail(`${k} isn’t an organisation setting`);
    if ('legal_name' in p) { if (!text(p.legal_name)) return fail('The business needs its legal name'); s.legal_name = text(p.legal_name)!; }
    for (const k of ['trading_name', 'registered_number', 'registered_office', 'address', 'contact_phone', 'website'] as const) if (k in p) s[k] = text(p[k]);
    if ('company_number' in p) s.registered_number = text(p.company_number);
    if ('contact_email' in p) {
      s.contact_email = text(p.contact_email);
      if (s.contact_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s.contact_email)) return fail('That email address doesn’t look right');
    }
    if (p.entity_type) s.entity_type = p.entity_type;
    if (p.jurisdiction && p.jurisdiction !== s.jurisdiction) return fail('The country was set when the books were created: it chose the chart of accounts and the VAT codes. Set up new books for another country.');
    if (p.base_currency && p.base_currency.toUpperCase() !== s.base_currency) {
      if (st.posted) return fail('The books’ currency is fixed once anything has been entered');
      s.base_currency = p.base_currency.toUpperCase();
    }
    if ('vat_registered' in p) s.vat_registered = Boolean(p.vat_registered);
    if ('vat_number' in p) {
      const v = checkVatNumber(p.vat_number ?? '', s.jurisdiction);
      if (v.error) return fail(v.error);
      s.vat_number = v.value;
    }
    if (s.vat_registered && !s.vat_number && ('vat_registered' in p || 'vat_number' in p)) return fail('Enter the VAT number, or say the business isn’t registered for VAT');
    if (p.vat_basis) s.vat_basis = p.vat_basis;
    const freq = p.vat_frequency ?? s.vat_frequency, stag = p.vat_period_start_month ?? s.vat_period_start_month;
    if (freq !== s.vat_frequency || stag !== s.vat_period_start_month) {
      if (!followsOn(s.last_vat_return_to, freq, stag)) {
        return fail(`VAT periods must follow on from the last return filed (to ${fmt(s.last_vat_return_to!)}).`);
      }
      s.vat_frequency = freq; s.vat_period_start_month = stag;
    }
    const ysm = p.year_start_month ?? s.year_start_month;
    const fy = p.first_year ?? Number(String(s.first_year).replace(/\D/g, ''));
    if (ysm !== s.year_start_month || `FY${fy}` !== s.first_year) {
      if (st.posted) return fail('The financial year is fixed now that entries have been posted. Ask your accountant about changing a year end.');
      if (!(fy >= 2000 && fy <= 2100)) return fail('The first year in the books is a year such as 2027');
      s.year_start_month = ysm; s.first_year = `FY${fy}`; s.first_year_starts = firstYearRange(ysm, fy).from;
    }
    if (p.setup_completed) s.setup_completed = true;
    if (p.contact_codes) {
      const n = { ...demoContactCodes, ...Object.fromEntries(Object.entries(p.contact_codes).filter(([, v]) => v !== undefined && v !== null)) };
      n.customer_prefix = String(n.customer_prefix).trim().toUpperCase();
      n.supplier_prefix = String(n.supplier_prefix).trim().toUpperCase();
      if (!/^[A-Z0-9]{0,6}$/.test(n.customer_prefix) || !/^[A-Z0-9]{0,6}$/.test(n.supplier_prefix)) return fail('A prefix is up to 6 letters and digits, such as C or CUST');
      if (n.mode === 'auto' && n.customer_prefix === n.supplier_prefix) return fail('Give customers and suppliers different prefixes, so their codes can’t be confused');
      if (!(n.digits >= 3 && n.digits <= 8)) return fail('The number part is 3 to 8 digits');
      Object.assign(demoContactCodes, { mode: n.mode, customer_prefix: n.customer_prefix, supplier_prefix: n.supplier_prefix, digits: n.digits });
      if (n.mode === 'auto') for (const c of demoContactsWithoutCode()) c.code = nextContactCode(Boolean(c.is_supplier && !c.is_customer));
    }
    st.s = withDerived(s, st.posted);
    return delay(withCodes(st.s));
  },
};
